/*eslint-disable no-unused-vars */
/*eslint-disable react-hooks/exhaustive-deps*/
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Badge, Button, Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader,
  Nav, NavItem, NavLink, Row, Table, TabContent, TabPane,
} from 'reactstrap';
import { toast } from 'react-toastify';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import { setLoadingOverlay } from '../../store/slices/loadingOverlaySlice';
import LocalstorageService from '../../helpers/localstorage-service';
import { CompanyId } from '../../constants';
import profitabilityService from '../../services/profitability';
import NoRecords from '../../components/Table/NoRecords';

const MONTHS_HE = ['', 'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
  'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];

const fmt = (v) => (v == null ? '—' : Number(v).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

// ── אינדיקטור התאמה ──────────────────────────────────────────
const matchIndicator = (actual, expected, tolerance, t) => {
  if (!expected || expected === 0) return null;
  const pct = Math.abs(actual - expected) / expected * 100;
  if (pct <= tolerance)         return { color: 'success', label: t('PROFITABILITY.INVOICE_SCAN_PAGE.ASSIGN_FORM.MATCH_LABEL') };
  if (pct <= tolerance * 3)     return { color: 'warning', label: t('PROFITABILITY.INVOICE_SCAN_PAGE.ASSIGN_FORM.SMALL_DIFF') };
  return                               { color: 'danger',  label: t('PROFITABILITY.INVOICE_SCAN_PAGE.ASSIGN_FORM.MISMATCH') };
};

// ── קריאת קובץ כ-base64 ──────────────────────────────────────
const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = (e) => resolve(e.target.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// ── בניית LinkedMonths ────────────────────────────────────────
const buildLinkedMonths = (baseMonth, baseYear, frequency) => {
  if (!frequency || frequency <= 1) return null;
  const months = [];
  for (let i = 0; i < frequency; i++) {
    let m = baseMonth - i;
    let y = baseYear;
    if (m <= 0) { m += 12; y -= 1; }
    months.unshift(`${y}-${String(m).padStart(2, '0')}`);
  }
  return months.join(',');
};

// ─────────────────────────────────────────────────────────────
const InvoiceScan = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [setPageTitle, setCrumbItem] = useOutletContext();
  const companyId = LocalstorageService.getItem(CompanyId);
  const now = new Date();

  const [activeTab,   setActiveTab]  = useState('assign');
  const [queue,       setQueue]      = useState([]);
  const [queueIdx,    setQueueIdx]   = useState(0);
  const [uploading,   setUploading]  = useState(false);
  const [isDragging,  setIsDragging] = useState(false);
  const [deleteItem,  setDeleteItem] = useState(null);  // מודל מחיקה

  // ── טאב שיוך — ברירת מחדל = חודש קודם (חשבוניות בגין החודש שעבר) ──────────
  const prevMonthScan = now.getMonth() === 0 ? 12 : now.getMonth();
  const prevYearScan  = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  const [month,        setMonth]        = useState(prevMonthScan);
  const [year,         setYear]         = useState(prevYearScan);
  const [tolerance,    setTolerance]    = useState(5);
  const [schedule,     setSchedule]     = useState([]);
  const [termSearch,   setTermSearch]   = useState('');
  const [selTerminal,  setSelTerminal]  = useState('');
  const [totalAmount,  setTotalAmount]  = useState('');
  const [linkedMonths, setLinkedMonths] = useState('');
  const [manualMonths, setManualMonths] = useState(false);
  const [assigning,    setAssigning]    = useState(false);
  const [assignType,       setAssignType]      = useState('terminal'); // 'terminal' | 'expense'
  const [expenseSources,   setExpenseSources]  = useState([]);
  const [selSourceId,      setSelSourceId]     = useState('');
  const [assigningExpense, setAssigningExpense] = useState(false);
  const [showTermList, setShowTermList] = useState(false);

  // ── טאב חיפוש — ברירת מחדל: מהחודש הקודם עד החודש הקודם ───────────────────
  const [searchFromMonth, setSearchFromMonth] = useState(prevMonthScan);
  const [searchFromYear,  setSearchFromYear]  = useState(prevYearScan);
  const [searchToMonth,   setSearchToMonth]   = useState(prevMonthScan);
  const [searchToYear,    setSearchToYear]    = useState(prevYearScan);
  // YYYY-MM computed values
  const searchFrom = `${searchFromYear}-${String(searchFromMonth).padStart(2, '0')}`;
  const searchTo   = `${searchToYear}-${String(searchToMonth).padStart(2, '0')}`;
  const [searchTerm,     setSearchTerm]     = useState('');
  const [searchAmtMin,   setSearchAmtMin]   = useState('');
  const [searchAmtMax,   setSearchAmtMax]   = useState('');
  const [invoices,       setInvoices]       = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);

  const fileInputRef = useRef(null);
  const termSearchRef = useRef(null);

  useEffect(() => {
    setPageTitle(t('PROFITABILITY.INVOICE_SCAN_PAGE.PAGE_TITLE'));
    setCrumbItem([
      { title: t('PROFITABILITY.INVOICE_SCAN_PAGE.CRUMB_PARENT'), link: '#' },
      { title: t('PROFITABILITY.INVOICE_SCAN_PAGE.CRUMB_ITEM'), link: '#' },
    ]);
  }, []);

  const fetchQueue = async () => {
    try {
      const res = await profitabilityService.getInvoiceQueue({ companyId });
      setQueue(res.data?.dataList || []);
      setQueueIdx(0);
    } catch {
      toast.error(t('PROFITABILITY.INVOICE_SCAN_PAGE.TOAST_ERROR_QUEUE'));
    }
  };

  const fetchSchedule = async () => {
    try {
      const res = await profitabilityService.getPaymentSchedule({ companyId, month, year });
      setSchedule(res.data?.dataList || []);
    } catch {
      toast.error(t('PROFITABILITY.INVOICE_SCAN_PAGE.TOAST_ERROR_SCHEDULE'));
    }
  };

  const fetchExpenseSources = async () => {
    try {
      const res = await profitabilityService.getExpenseSources({ companyId });
      setExpenseSources(res.data?.dataList || []);
    } catch { /* שגיאה שקטה — הספקים לא קריטיים לדף */ }
  };

  // המרת "YYYY-MM" ל-YYYYMM INT
  const toYearMonth = (str) => {
    if (!str) return null;
    const [y, m] = str.split('-');
    return parseInt(y) * 100 + parseInt(m);
  };

  const fetchInvoices = async () => {
    dispatch(setLoadingOverlay(true));
    try {
      const startYearMonth = toYearMonth(searchFrom);
      const endYearMonth   = toYearMonth(searchTo);
      const res = await profitabilityService.getInvoices({
        companyId,
        month: 0, year: 0,
        startYearMonth, endYearMonth,
      });
      setInvoices(res.data?.dataList || []);
    } catch {
      toast.error(t('PROFITABILITY.INVOICE_SCAN_PAGE.TOAST_ERROR_INVOICES'));
    } finally {
      dispatch(setLoadingOverlay(false));
    }
  };

  useEffect(() => { fetchQueue(); fetchExpenseSources(); }, []);
  useEffect(() => { fetchSchedule(); }, [month, year]);
  // טאב חיפוש — נטען רק בלחיצת "חפש"

  // ── סינון חיפוש client-side ──────────────────────────────
  useEffect(() => {
    let list = invoices;
    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      list = list.filter(r =>
        (r.terminalNumber || '').toLowerCase().includes(q) ||
        (r.businessName   || '').toLowerCase().includes(q)
      );
    }
    if (searchAmtMin !== '') list = list.filter(r => (r.totalAmount || r.amountIncVat || 0) >= Number(searchAmtMin));
    if (searchAmtMax !== '') list = list.filter(r => (r.totalAmount || r.amountIncVat || 0) <= Number(searchAmtMax));
    setFilteredInvoices(list);
  }, [invoices, searchTerm, searchAmtMin, searchAmtMax]);

  // ── מסוף נבחר ────────────────────────────────────────────
  const selectedRow = schedule.find(r => r.terminalNumber === selTerminal);

  useEffect(() => {
    if (!manualMonths && selectedRow) {
      const freq = selectedRow.paymentFrequency || 1;
      setLinkedMonths(buildLinkedMonths(month, year, freq) || '');
    }
  }, [selTerminal, month, year, manualMonths, schedule]);

  const expectedTotal = selectedRow
    ? (selectedRow.fixPayment || 0) + (selectedRow.estimatedPerBeat || 0)
    : 0;
  const match = totalAmount && expectedTotal > 0
    ? matchIndicator(Number(totalAmount), expectedTotal, tolerance, t)
    : null;

  // ── רשימת מסופים מסוננת לפי חיפוש ──────────────────────
  const filteredSchedule = termSearch.trim()
    ? schedule.filter(r =>
        (r.terminalNumber || '').includes(termSearch) ||
        (r.locationName   || '').toLowerCase().includes(termSearch.toLowerCase())
      )
    : schedule;

  // שם המסוף הנבחר להצגה
  const selLabel = selectedRow
    ? `${selectedRow.terminalNumber} — ${selectedRow.locationName}`
    : `— ${t('PROFITABILITY.INVOICE_SCAN_PAGE.ASSIGN_FORM.TERMINAL_PLACEHOLDER')} —`;

  // ── העלאת קבצים ──────────────────────────────────────────
  const uploadFiles = async (files) => {
    const arr = Array.from(files).filter(f => /\.(pdf|jpg|jpeg|png)$/i.test(f.name));
    if (arr.length === 0) {
      toast.warning(t('PROFITABILITY.INVOICE_SCAN_PAGE.TOAST_NO_FILES'));
      return;
    }
    setUploading(true);
    let uploaded = 0;
    for (const file of arr) {
      try {
        const base64 = await readFileAsBase64(file);
        await profitabilityService.uploadInvoiceFile({ companyId, fileContent: base64, fileName: file.name });
        uploaded++;
      } catch {
        toast.error(t('PROFITABILITY.INVOICE_SCAN_PAGE.TOAST_UPLOAD_ERROR', { filename: file.name }));
      }
    }
    setUploading(false);
    if (uploaded > 0) {
      toast.success(t('PROFITABILITY.INVOICE_SCAN_PAGE.TOAST_UPLOADED', { count: uploaded }));
      await fetchQueue();
    }
  };

  const onDrop = useCallback((e) => {
    e.preventDefault(); setIsDragging(false);
    uploadFiles(e.dataTransfer.files);
  }, [companyId]);

  const onDragOver  = useCallback((e) => { e.preventDefault(); setIsDragging(true); }, []);
  const onDragLeave = useCallback(() => setIsDragging(false), []);

  // ── שיוך ─────────────────────────────────────────────────
  const handleAssign = async () => {
    const item = queue[queueIdx];
    if (!item)        return;
    if (!selTerminal)                     { toast.warning(t('PROFITABILITY.INVOICE_SCAN_PAGE.TOAST_WARN_TERMINAL')); return; }
    if (!totalAmount || Number(totalAmount) <= 0) { toast.warning(t('PROFITABILITY.INVOICE_SCAN_PAGE.TOAST_WARN_AMOUNT')); return; }

    setAssigning(true);
    try {
      await profitabilityService.assignInvoiceFile({
        companyId,
        queueId:       item.id,
        blobPath:      item.blobPath,
        terminalNumber: selTerminal,
        businessName:  selectedRow?.locationName || selTerminal,
        totalAmount:   Number(totalAmount),
        month, year,
        linkedMonths:  linkedMonths || null,
      });
      toast.success(t('PROFITABILITY.INVOICE_SCAN_PAGE.TOAST_ASSIGNED'));
      setSelTerminal(''); setTotalAmount(''); setLinkedMonths('');
      setManualMonths(false); setTermSearch('');
      await fetchQueue();
    } catch {
      toast.error(t('PROFITABILITY.INVOICE_SCAN_PAGE.TOAST_ERROR_ASSIGN'));
    } finally {
      setAssigning(false);
    }
  };

  const handleAssignAsExpense = async () => {
    const item = queue[queueIdx];
    if (!item) return;
    if (!selSourceId)                              { toast.warning(t('PROFITABILITY.INVOICE_SCAN_PAGE.TOAST_WARN_SOURCE')); return; }
    if (!totalAmount || Number(totalAmount) <= 0) { toast.warning(t('PROFITABILITY.INVOICE_SCAN_PAGE.TOAST_WARN_AMOUNT')); return; }

    setAssigningExpense(true);
    try {
      const selectedSource = expenseSources.find(s => s.id === Number(selSourceId));
      await profitabilityService.assignInvoiceAsExpense({
        companyId,
        queueId:         item.id,
        blobPath:        item.blobPath,
        expenseSourceId: Number(selSourceId),
        sourceName:      selectedSource?.sourceName || '',
        totalAmount:     Number(totalAmount),
        month,
        year,
      });
      toast.success(t('PROFITABILITY.INVOICE_SCAN_PAGE.TOAST_ASSIGNED_EXPENSE'));
      setSelSourceId('');
      setTotalAmount('');
      await fetchQueue();
    } catch {
      toast.error(t('PROFITABILITY.INVOICE_SCAN_PAGE.TOAST_ERROR_ASSIGN'));
    } finally {
      setAssigningExpense(false);
    }
  };

  const handleDeleteFromQueue = (item) => {
    setDeleteItem(item);  // פותח מודל אישור
  };

  const confirmDelete = async () => {
    const item = deleteItem;
    setDeleteItem(null);
    try {
      await profitabilityService.deleteInvoiceQueueItem({ id: item.id, companyId });
      toast.success(t('PROFITABILITY.INVOICE_SCAN_PAGE.TOAST_DELETED_QUEUE'));
      await fetchQueue();
    } catch {
      toast.error(t('PROFITABILITY.INVOICE_SCAN_PAGE.TOAST_ERROR_DELETE'));
    }
  };

  const openInvoiceFile = async (id) => {
    try {
      const res = await profitabilityService.getInvoiceFileUrl({ id, companyId });
      const url = res.data?.data?.url;
      if (url) window.open(url, '_blank');
      else toast.error(t('PROFITABILITY.INVOICE_SCAN_PAGE.TOAST_NO_FILE'));
    } catch {
      toast.error(t('PROFITABILITY.INVOICE_SCAN_PAGE.TOAST_ERROR_FILE'));
    }
  };

  const currentItem = queue[queueIdx] || null;
  const yearOptions = [];
  for (let y = now.getFullYear() + 1; y >= now.getFullYear() - 3; y--) yearOptions.push(y);
  const isPdf  = (name) => /\.pdf$/i.test(name || '');
  const isImage = (name) => /\.(jpg|jpeg|png)$/i.test(name || '');

  const linkedMonthsDisplay = linkedMonths
    ? linkedMonths.split(',').map(m => {
        const [y, mo] = m.split('-');
        return `${MONTHS_HE[parseInt(mo)]} ${y}`;
      }).join(' • ')
    : null;

  return (
    <React.Fragment>
      <Nav tabs className="mb-3">
        <NavItem>
          <NavLink className={classnames({ active: activeTab === 'assign' })}
            style={{ cursor: 'pointer' }} onClick={() => setActiveTab('assign')}>
            {t('PROFITABILITY.INVOICE_SCAN_PAGE.TAB_ASSIGN')}
            {queue.length > 0 && <Badge color="warning" className="ms-2">{queue.length}</Badge>}
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink className={classnames({ active: activeTab === 'search' })}
            style={{ cursor: 'pointer' }} onClick={() => setActiveTab('search')}>
            {t('PROFITABILITY.INVOICE_SCAN_PAGE.TAB_SEARCH')}
          </NavLink>
        </NavItem>
      </Nav>

      <TabContent activeTab={activeTab}>

        {/* ── טאב שיוך ──────────────────────────────────── */}
        <TabPane tabId="assign">

          {/* אזור העלאה — קומפקטי כשיש פריטים בתור */}
          <div
            className={`border rounded mb-2 ${queue.length > 0 ? 'py-1 px-3 d-flex align-items-center justify-content-center gap-2' : 'p-3 text-center'} ${isDragging ? 'bg-primary bg-opacity-10 border-primary' : 'bg-light border-secondary'}`}
            style={{ cursor: 'pointer', borderStyle: 'dashed', position: 'relative', zIndex: 2 }}
            onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}
            onClick={() => fileInputRef.current?.click()}
          >
            <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png"
              multiple style={{ display: 'none' }}
              onChange={e => uploadFiles(e.target.files)} />
            {uploading
              ? <span><i className="ri-loader-4-line me-1" />{t('PROFITABILITY.INVOICE_SCAN_PAGE.UPLOADING')}</span>
              : <span><i className={`ri-upload-cloud-2-line me-1 ${queue.length > 0 ? '' : 'fs-5'}`} />{t('PROFITABILITY.INVOICE_SCAN_PAGE.UPLOAD_AREA')}</span>}
          </div>

          {queue.length === 0 ? (
            <div className="text-center text-muted py-4">
              <i className="ri-inbox-2-line fs-1 d-block" />
              {t('PROFITABILITY.INVOICE_SCAN_PAGE.EMPTY_QUEUE')}
            </div>
          ) : (
            <Row>
              {/* ── תצוגה מקדימה + ניווט ─────────────────── */}
              <Col md={5}>

                {/* ── פס ניווט ── */}
                <div className="d-flex align-items-center justify-content-between mb-2 gap-2">
                  <Button
                    size="md" color="secondary" outline
                    disabled={queueIdx === 0}
                    onClick={() => setQueueIdx(i => i - 1)}
                    style={{ minWidth: 44, minHeight: 38 }}>
                    <i className="ri-arrow-right-s-line fs-5" />
                  </Button>

                  <div className="text-center flex-grow-1">
                    <div className="fw-semibold text-truncate" style={{ maxWidth: '100%', fontSize: 13 }}
                      title={currentItem?.originalFileName}>
                      {currentItem?.originalFileName}
                    </div>
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      {t('PROFITABILITY.INVOICE_SCAN_PAGE.PREVIEW_LABEL', { current: queueIdx + 1, total: queue.length })}
                      {queue.length > 1 && (
                        <span className="ms-1 text-warning">
                          {t('PROFITABILITY.INVOICE_SCAN_PAGE.PREVIEW_REMAINING', { count: queue.length - queueIdx - 1 })}
                        </span>
                      )}
                    </div>
                  </div>

                  <Button
                    size="md" color="secondary" outline
                    disabled={queueIdx >= queue.length - 1}
                    onClick={() => setQueueIdx(i => i + 1)}
                    style={{ minWidth: 44, minHeight: 38 }}>
                    <i className="ri-arrow-left-s-line fs-5" />
                  </Button>
                </div>

                {/* ── תצוגה מקדימה ── */}
                {currentItem?.previewUrl ? (
                  isPdf(currentItem.originalFileName) ? (
                    <embed
                      src={currentItem.previewUrl}
                      type="application/pdf"
                      style={{ width: '100%', height: 'calc(100vh - 220px)', borderRadius: 4, border: '1px solid #ccc' }}
                    />
                  ) : (
                    <img src={currentItem.previewUrl}
                      alt={t('PROFITABILITY.INVOICE_SCAN_PAGE.PREVIEW_LABEL', { current: queueIdx + 1, total: queue.length })}
                      className="img-fluid rounded border"
                      style={{ maxHeight: 'calc(100vh - 220px)', width: '100%', objectFit: 'contain' }}
                    />
                  )
                ) : (
                  <div className="border rounded d-flex align-items-center justify-content-center bg-light"
                    style={{ height: 'calc(100vh - 220px)' }}>
                    <div className="text-center text-muted">
                      <i className="ri-file-text-line fs-1 d-block mb-2" />
                      <small>{t('PROFITABILITY.INVOICE_SCAN_PAGE.PREVIEW_NONE')}</small>
                      {currentItem?.previewUrl === undefined && <div><small>{t('PROFITABILITY.INVOICE_SCAN_PAGE.PREVIEW_LOADING')}</small></div>}
                    </div>
                  </div>
                )}

                <Button color="danger" outline size="sm" className="mt-2 w-100"
                  onClick={() => handleDeleteFromQueue(currentItem)}>
                  <i className="ri-delete-bin-line me-1" />{t('PROFITABILITY.INVOICE_SCAN_PAGE.DELETE_FROM_QUEUE')}
                </Button>
              </Col>

              {/* ── טופס שיוך ────────────────────────────── */}
              <Col md={7}>
                <div className="card p-3">
                  <h6 className="mb-3"><i className="ri-links-line me-1" />{t('PROFITABILITY.INVOICE_SCAN_PAGE.ASSIGN_FORM.TITLE')}</h6>

                  {/* סוג שיוך */}
                  <div className="mb-3 d-flex gap-2">
                    <button
                      type="button"
                      className={`btn btn-sm flex-fill ${assignType === 'terminal' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setAssignType('terminal')}>
                      <i className="ri-bank-card-line me-1" />{t('PROFITABILITY.INVOICE_SCAN_PAGE.ASSIGN_FORM.TYPE_TERMINAL')}
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm flex-fill ${assignType === 'expense' ? 'btn-warning' : 'btn-outline-warning'}`}
                      onClick={() => setAssignType('expense')}>
                      <i className="ri-store-2-line me-1" />{t('PROFITABILITY.INVOICE_SCAN_PAGE.ASSIGN_FORM.TYPE_EXPENSE')}
                    </button>
                  </div>

                  {assignType === 'terminal' && (
                    <>
                      {/* סובלנות */}
                      <Row className="mb-2 align-items-center">
                        <Col xs="auto"><Label className="mb-0 small">{t('PROFITABILITY.INVOICE_SCAN_PAGE.ASSIGN_FORM.TOLERANCE')}</Label></Col>
                        <Col xs={4}>
                          <Input type="number" size="sm" min={0} max={50}
                            value={tolerance} onChange={e => setTolerance(Number(e.target.value))} />
                        </Col>
                      </Row>

                      {/* חודש / שנה */}
                      <Row className="mb-3">
                        <Col>
                          <Label className="mb-1 small">{t('PROFITABILITY.INVOICE_SCAN_PAGE.ASSIGN_FORM.MONTH')}</Label>
                          <select className="form-select form-select-sm" value={month}
                            onChange={e => { setMonth(+e.target.value); setManualMonths(false); }}>
                            {MONTHS_HE.slice(1).map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                          </select>
                        </Col>
                        <Col>
                          <Label className="mb-1 small">{t('PROFITABILITY.INVOICE_SCAN_PAGE.ASSIGN_FORM.YEAR')}</Label>
                          <select className="form-select form-select-sm" value={year}
                            onChange={e => { setYear(+e.target.value); setManualMonths(false); }}>
                            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                          </select>
                        </Col>
                      </Row>

                      {/* ── מסוף — חיפוש + רשימה ── */}
                      <div className="mb-3 position-relative">
                        <Label className="mb-1 small">{t('PROFITABILITY.INVOICE_SCAN_PAGE.ASSIGN_FORM.TERMINAL_LABEL')}</Label>
                        <div className="input-group input-group-sm">
                          <Input
                            type="text"
                            placeholder={t('PROFITABILITY.INVOICE_SCAN_PAGE.ASSIGN_FORM.TERMINAL_PLACEHOLDER')}
                            value={selTerminal ? selLabel : termSearch}
                            onFocus={() => { setShowTermList(true); if (selTerminal) { setTermSearch(''); setSelTerminal(''); } }}
                            onChange={e => { setTermSearch(e.target.value); setSelTerminal(''); setShowTermList(true); }}
                            ref={termSearchRef}
                            autoComplete="off"
                          />
                          {selTerminal && (
                            <Button size="sm" color="secondary" outline
                              onClick={() => { setSelTerminal(''); setTermSearch(''); setShowTermList(true); termSearchRef.current?.focus(); }}>
                              <i className="ri-close-line" />
                            </Button>
                          )}
                        </div>

                        {/* רשימה נפתחת */}
                        {showTermList && !selTerminal && (
                          <div className="position-absolute w-100 bg-white border rounded shadow-sm"
                            style={{ zIndex: 1000, maxHeight: 220, overflowY: 'auto', top: '100%' }}>
                            {filteredSchedule.length === 0 ? (
                              <div className="p-2 text-muted small text-center">{t('PROFITABILITY.INVOICE_SCAN_PAGE.ASSIGN_FORM.TERMINAL_NOT_FOUND')}</div>
                            ) : filteredSchedule.map(r => (
                              <div key={r.terminalNumber}
                                className="px-3 py-2 border-bottom"
                                style={{ cursor: 'pointer', fontSize: 13 }}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  setSelTerminal(r.terminalNumber);
                                  setTermSearch('');
                                  setManualMonths(false);
                                  setShowTermList(false);
                                }}>
                                <strong>{r.terminalNumber}</strong>
                                <span className="ms-2 text-muted">{r.locationName}</span>
                                <span className="ms-2 text-info small">
                                  {fmt((r.fixPayment||0)+(r.estimatedPerBeat||0))} ₪
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* חודשים מכוסים — לתשלום רבעוני+ */}
                      {selectedRow && (selectedRow.paymentFrequency || 1) > 1 && (
                        <div className="mb-3">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <span className="small text-muted">{t('PROFITABILITY.INVOICE_SCAN_PAGE.ASSIGN_FORM.COVERED_MONTHS')}</span>
                            {!manualMonths && linkedMonthsDisplay && (
                              <span className="small text-secondary">{linkedMonthsDisplay}</span>
                            )}
                            <Button size="sm" color="link" className="p-0 small"
                              onClick={() => setManualMonths(m => !m)}>
                              {manualMonths ? t('PROFITABILITY.INVOICE_SCAN_PAGE.ASSIGN_FORM.AUTO_MODE') : t('PROFITABILITY.INVOICE_SCAN_PAGE.ASSIGN_FORM.MANUAL_MODE')}
                            </Button>
                          </div>
                          {manualMonths && (
                            <Input type="text" size="sm"
                              placeholder={t('PROFITABILITY.INVOICE_SCAN_PAGE.ASSIGN_FORM.LINKED_MONTHS_PLACEHOLDER')}
                              value={linkedMonths}
                              onChange={e => setLinkedMonths(e.target.value)} />
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {assignType === 'expense' && (
                    <>
                      {/* חודש / שנה */}
                      <Row className="mb-3">
                        <Col>
                          <Label className="mb-1 small">{t('PROFITABILITY.INVOICE_SCAN_PAGE.ASSIGN_FORM.MONTH')}</Label>
                          <select className="form-select form-select-sm" value={month}
                            onChange={e => setMonth(+e.target.value)}>
                            {MONTHS_HE.slice(1).map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                          </select>
                        </Col>
                        <Col>
                          <Label className="mb-1 small">{t('PROFITABILITY.INVOICE_SCAN_PAGE.ASSIGN_FORM.YEAR')}</Label>
                          <select className="form-select form-select-sm" value={year}
                            onChange={e => setYear(+e.target.value)}>
                            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                          </select>
                        </Col>
                      </Row>

                      {/* ספק */}
                      <div className="mb-3">
                        <Label className="mb-1 small">{t('PROFITABILITY.INVOICE_SCAN_PAGE.ASSIGN_FORM.SOURCE_LABEL')}</Label>
                        <select className="form-select form-select-sm" value={selSourceId}
                          onChange={e => setSelSourceId(e.target.value)}>
                          <option value="">{t('PROFITABILITY.INVOICE_SCAN_PAGE.ASSIGN_FORM.SOURCE_PLACEHOLDER')}</option>
                          {expenseSources.map(s => (
                            <option key={s.id} value={s.id}>{s.sourceName}</option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}

                  {/* סכום */}
                  <div className="mb-3">
                    <Label className="mb-1 small">{t('PROFITABILITY.INVOICE_SCAN_PAGE.ASSIGN_FORM.AMOUNT_LABEL')}</Label>
                    <Input type="number" size="sm" min={0} step="0.01"
                      value={totalAmount} onChange={e => setTotalAmount(e.target.value)}
                      placeholder={t('PROFITABILITY.INVOICE_SCAN_PAGE.ASSIGN_FORM.AMOUNT_PLACEHOLDER')} />
                  </div>

                  {/* אינדיקטור התאמה */}
                  {match && (
                    <div className={`alert alert-${match.color} py-2 px-3 small mb-3`}>
                      {match.label} — {t('PROFITABILITY.INVOICE_SCAN_PAGE.ASSIGN_FORM.EXPECTED_LABEL')} <strong>{fmt(expectedTotal)} ₪</strong>
                      {Number(totalAmount) !== expectedTotal && (
                        <span className="ms-2">{t('PROFITABILITY.INVOICE_SCAN_PAGE.ASSIGN_FORM.DIFF_LABEL')} {fmt(Math.abs(Number(totalAmount) - expectedTotal))} ₪)</span>
                      )}
                    </div>
                  )}
                  {totalAmount && !match && expectedTotal > 0 && (
                    <div className="text-muted small mb-3">
                      {t('PROFITABILITY.INVOICE_SCAN_PAGE.ASSIGN_FORM.EXPECTED_LABEL')} <strong>{fmt(expectedTotal)} ₪</strong>
                    </div>
                  )}

                  <Button color="success"
                    disabled={assignType === 'terminal'
                      ? (assigning || !selTerminal || !totalAmount)
                      : (assigningExpense || !selSourceId || !totalAmount)}
                    onClick={assignType === 'terminal' ? handleAssign : handleAssignAsExpense}>
                    {(assignType === 'terminal' ? assigning : assigningExpense)
                      ? <><i className="ri-loader-4-line me-1" />{t('PROFITABILITY.INVOICE_SCAN_PAGE.ASSIGN_FORM.ASSIGNING_BUTTON')}</>
                      : <><i className="ri-checkbox-circle-line me-1" />{t('PROFITABILITY.INVOICE_SCAN_PAGE.ASSIGN_FORM.ASSIGN_BUTTON')}</>}
                  </Button>
                </div>
              </Col>
            </Row>
          )}
        </TabPane>

        {/* ── טאב חיפוש ─────────────────────────────────── */}
        <TabPane tabId="search">

          {/* ── פס סינון ── */}
          <div className="card p-3 mb-3">
            <Row className="g-2 align-items-end">
              {/* מ-תאריך */}
              <Col xs={6} md={2}>
                <label className="form-label mb-1 small">{t('PROFITABILITY.INVOICE_SCAN_PAGE.SEARCH.FROM_DATE')}</label>
                <div className="d-flex gap-1">
                  <select className="form-select form-select-sm" value={searchFromMonth}
                    onChange={e => setSearchFromMonth(+e.target.value)} style={{ flex: 1 }}>
                    {MONTHS_HE.slice(1).map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                  </select>
                  <select className="form-select form-select-sm" value={searchFromYear}
                    onChange={e => setSearchFromYear(+e.target.value)} style={{ width: 80 }}>
                    {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </Col>
              {/* עד תאריך */}
              <Col xs={6} md={2}>
                <label className="form-label mb-1 small">{t('PROFITABILITY.INVOICE_SCAN_PAGE.SEARCH.TO_DATE')}</label>
                <div className="d-flex gap-1">
                  <select className="form-select form-select-sm" value={searchToMonth}
                    onChange={e => setSearchToMonth(+e.target.value)} style={{ flex: 1 }}>
                    {MONTHS_HE.slice(1).map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                  </select>
                  <select className="form-select form-select-sm" value={searchToYear}
                    onChange={e => setSearchToYear(+e.target.value)} style={{ width: 80 }}>
                    {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <label className="form-label mb-1 small">{t('PROFITABILITY.INVOICE_SCAN_PAGE.SEARCH.TERMINAL_LABEL')}</label>
                <Input type="text" size="sm" placeholder={t('PROFITABILITY.INVOICE_SCAN_PAGE.SEARCH.TERMINAL_PLACEHOLDER')}
                  value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && fetchInvoices()} />
              </Col>
              <Col xs={5} md={2}>
                <label className="form-label mb-1 small">{t('PROFITABILITY.INVOICE_SCAN_PAGE.SEARCH.MIN_AMOUNT')}</label>
                <Input type="number" size="sm" min={0} placeholder="0"
                  value={searchAmtMin} onChange={e => setSearchAmtMin(e.target.value)} />
              </Col>
              <Col xs={5} md={2}>
                <label className="form-label mb-1 small">{t('PROFITABILITY.INVOICE_SCAN_PAGE.SEARCH.MAX_AMOUNT')}</label>
                <Input type="number" size="sm" min={0} placeholder="∞"
                  value={searchAmtMax} onChange={e => setSearchAmtMax(e.target.value)} />
              </Col>
              <Col xs={2} md={1} className="d-flex align-items-end">
                <Button color="primary" size="sm" className="w-100"
                  onClick={fetchInvoices} title={t('PROFITABILITY.INVOICE_SCAN_PAGE.SEARCH.BTN_SEARCH')}>
                  <i className="ri-search-line" />
                </Button>
              </Col>
            </Row>
            {invoices.length > 0 && (
              <div className="text-muted small mt-2">
                <strong>{filteredInvoices.length}</strong> {t('PROFITABILITY.INVOICE_SCAN_PAGE.SEARCH.WITH_FILE').replace(' עם קובץ', ' רשומות')}
                {filteredInvoices.filter(r => r.blobPath).length > 0 && (
                  <span className="ms-2 text-success">
                    ({filteredInvoices.filter(r => r.blobPath).length} {t('PROFITABILITY.INVOICE_SCAN_PAGE.SEARCH.WITH_FILE')})
                  </span>
                )}
                {(searchTerm || searchAmtMin || searchAmtMax) && invoices.length !== filteredInvoices.length && (
                  <span className="ms-2 text-warning">
                    ({t('PROFITABILITY.INVOICE_SCAN_PAGE.SEARCH.FILTERED_FROM', { total: invoices.length })})
                  </span>
                )}
              </div>
            )}
          </div>

          {filteredInvoices.length === 0 ? <NoRecords /> : (
            <div className="table-responsive" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              <Table bordered hover size="sm">
                <thead className="table-dark">
                  <tr>
                    <th>{t('PROFITABILITY.INVOICE_SCAN_PAGE.SEARCH.TABLE.TERMINAL')}</th>
                    <th>{t('PROFITABILITY.INVOICE_SCAN_PAGE.SEARCH.TABLE.BUSINESS_NAME')}</th>
                    <th>{t('PROFITABILITY.INVOICE_SCAN_PAGE.SEARCH.TABLE.COVERED_MONTHS')}</th>
                    <th className="text-end">{t('PROFITABILITY.INVOICE_SCAN_PAGE.SEARCH.TABLE.AMOUNT')}</th>
                    <th>{t('PROFITABILITY.INVOICE_SCAN_PAGE.SEARCH.TABLE.FILE')}</th>
                    <th>{t('PROFITABILITY.INVOICE_SCAN_PAGE.SEARCH.TABLE.UPDATED')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map(r => (
                    <tr key={r.id} className={r.received ? '' : 'table-warning'}>
                      <td>{r.terminalNumber}</td>
                      <td>{r.businessName}</td>
                      <td>
                        {r.linkedMonths
                          ? r.linkedMonths.split(',').map(m => {
                              const [y, mo] = m.split('-');
                              return (
                                <Badge key={m} color="secondary" className="me-1 small">
                                  {MONTHS_HE[parseInt(mo)]} {y}
                                </Badge>
                              );
                            })
                          : <span className="text-muted small">{searchFrom}</span>}
                      </td>
                      <td className="text-end fw-bold">{fmt(r.totalAmount || r.amountIncVat)}</td>
                      <td>
                        {r.blobPath ? (
                          <Button size="sm" color="info" outline onClick={() => openInvoiceFile(r.id)}
                            title={t('PROFITABILITY.INVOICES_PAGE.OPEN_FILE')}>
                            <i className="ri-eye-line" />
                          </Button>
                        ) : <span className="text-muted">—</span>}
                      </td>
                      <td className="text-muted small">
                        {r.updatedDate ? new Date(r.updatedDate).toLocaleDateString('he-IL') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </TabPane>
      </TabContent>

      {/* סגירת dropdown בלחיצה מחוץ */}
      {showTermList && !selTerminal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 999 }}
          onClick={() => setShowTermList(false)} />
      )}

      {/* ── מודל אישור מחיקה ── */}
      <Modal isOpen={!!deleteItem} toggle={() => setDeleteItem(null)} centered size="sm">
        <ModalHeader toggle={() => setDeleteItem(null)} className="border-0 pb-0">
          <i className="ri-delete-bin-line text-danger me-2" />
          {t('PROFITABILITY.INVOICE_SCAN_PAGE.DELETE_MODAL.TITLE')}
        </ModalHeader>
        <ModalBody className="text-center py-3">
          <div className="bg-danger bg-opacity-10 rounded p-3 mb-3">
            <i className="ri-file-warning-line text-danger fs-2 d-block mb-2" />
            <div className="fw-semibold text-truncate" style={{ maxWidth: 280, margin: '0 auto' }}
              title={deleteItem?.originalFileName}>
              {deleteItem?.originalFileName}
            </div>
          </div>
          <p className="text-muted mb-0 small">{t('PROFITABILITY.INVOICE_SCAN_PAGE.DELETE_MODAL.BODY')}</p>
        </ModalBody>
        <ModalFooter className="border-0 pt-0 justify-content-center gap-2">
          <Button color="danger" onClick={confirmDelete}>
            <i className="ri-delete-bin-line me-1" />
            {t('PROFITABILITY.INVOICE_SCAN_PAGE.DELETE_MODAL.CONFIRM')}
          </Button>
          <Button color="light" onClick={() => setDeleteItem(null)}>
            {t('PROFITABILITY.COMMON.CANCEL')}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default InvoiceScan;
